import type { GraphPath } from './graph.js';
import { isPlayableStoryPath, storyPathKey } from './story-playback.js';

export const DEFAULT_EXPLORATION_TRAIL_LIMIT = 12;

export type ExplorationTrailPersonEntry = {
  id: string;
  kind: 'person';
  personId: string;
  displayLabel: string;
};

export type ExplorationTrailPathEntry = {
  id: string;
  kind: 'path';
  sourceId: string;
  targetId: string;
  sourceLabel: string;
  targetLabel: string;
  displayLabel: string;
  stepCount: number;
  pathKey: string;
  peopleIds: readonly string[];
  relationshipIds: readonly string[];
};

export type ExplorationTrailEntry = ExplorationTrailPersonEntry | ExplorationTrailPathEntry;

export type ExplorationTrailState = {
  entries: readonly ExplorationTrailEntry[];
  maxEntries: number;
  nextSequence: number;
};

export type ExplorationTrailAction =
  | { type: 'visit-person'; personId: string; displayLabel: string }
  | {
      type: 'complete-path';
      path: GraphPath;
      sourceLabel: string;
      targetLabel: string;
    }
  | { type: 'clear' };

/**
 * A jump target is an intent for the canonical atlas transition boundary. The
 * trail deliberately does not mutate URLs or become a second history owner.
 */
export type ExplorationTrailJumpTarget =
  | { kind: 'person'; personId: string }
  | {
      kind: 'path';
      sourceId: string;
      targetId: string;
      pathKey: string;
      peopleIds: readonly string[];
      relationshipIds: readonly string[];
    };

function publicLabel(value: string): string | null {
  const normalized = value.trim().replace(/\s+/g, ' ');
  return normalized.length > 0 ? normalized : null;
}

function semanticEntryKey(entry: ExplorationTrailEntry): string {
  return entry.kind === 'person' ? `person:${entry.personId}` : `path:${entry.pathKey}`;
}

function appendEntry(
  state: ExplorationTrailState,
  entry: Omit<ExplorationTrailPersonEntry, 'id'> | Omit<ExplorationTrailPathEntry, 'id'>,
): ExplorationTrailState {
  const previous = state.entries.at(-1);
  const nextEntry = { ...entry, id: `trail-${state.nextSequence}` };
  if (previous !== undefined && semanticEntryKey(previous) === semanticEntryKey(nextEntry)) {
    return state;
  }

  const entries = [...state.entries, nextEntry].slice(-state.maxEntries);
  return { ...state, entries, nextSequence: state.nextSequence + 1 };
}

export function createExplorationTrailState(
  maxEntries = DEFAULT_EXPLORATION_TRAIL_LIMIT,
): ExplorationTrailState {
  if (!Number.isSafeInteger(maxEntries) || maxEntries < 1) {
    throw new RangeError('Exploration trail limit must be a positive safe integer.');
  }
  return { entries: [], maxEntries, nextSequence: 0 };
}

export function explorationTrailReducer(
  state: ExplorationTrailState,
  action: ExplorationTrailAction,
): ExplorationTrailState {
  switch (action.type) {
    case 'visit-person': {
      const personId = action.personId.trim();
      const displayLabel = publicLabel(action.displayLabel);
      if (personId.length === 0 || displayLabel === null) {
        return state;
      }
      return appendEntry(state, { kind: 'person', personId, displayLabel });
    }

    case 'complete-path': {
      const sourceLabel = publicLabel(action.sourceLabel);
      const targetLabel = publicLabel(action.targetLabel);
      const pathKey = storyPathKey(action.path);
      if (
        sourceLabel === null ||
        targetLabel === null ||
        pathKey === null ||
        !isPlayableStoryPath(action.path)
      ) {
        return state;
      }
      const sourceId = action.path.people[0];
      const targetId = action.path.people.at(-1);
      if (sourceId === undefined || targetId === undefined) {
        return state;
      }
      return appendEntry(state, {
        kind: 'path',
        sourceId,
        targetId,
        sourceLabel,
        targetLabel,
        displayLabel: `${sourceLabel} \u2192 ${targetLabel}`,
        stepCount: action.path.relationships.length,
        pathKey,
        peopleIds: [...action.path.people],
        relationshipIds: action.path.relationships.map(({ id }) => id),
      });
    }

    case 'clear':
      return state.entries.length === 0 ? state : { ...state, entries: [] };
  }
}

export function explorationTrailJumpTarget(
  state: ExplorationTrailState,
  entryId: string,
): ExplorationTrailJumpTarget | null {
  const entry = state.entries.find(({ id }) => id === entryId);
  if (entry === undefined) {
    return null;
  }
  return entry.kind === 'person'
    ? { kind: 'person', personId: entry.personId }
    : {
        kind: 'path',
        sourceId: entry.sourceId,
        targetId: entry.targetId,
        pathKey: entry.pathKey,
        peopleIds: entry.peopleIds,
        relationshipIds: entry.relationshipIds,
      };
}
